import { useCallback, useState } from 'react';
import {
  Button,
  Card,
  CurrencyFormat,
  Icon,
  Spinner,
} from '@/components/generic';
import { useRequestReferralWithdrawal } from '@xhype/card-app-sdk';
import { useGetReferralBalance } from '@package/smart-wallet/hooks';
import { useAuthStore } from '@package/smart-wallet/store';
import { ethers } from 'ethers';

const ClaimRewards = () => {
  const id = useAuthStore((store) => store.id);
  const { isLoading, balance: rawBalance, refetch } = useGetReferralBalance();
  const balance = Number(rawBalance) || 0;
  const [isClaiming, setIsClaiming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const requestWithdrawal = useRequestReferralWithdrawal({
    onSuccess: () => {
      setSuccess(true);
      setIsClaiming(false);
      refetch();
      setTimeout(() => setSuccess(false), 3000);
    },
    onError: (error: any) => {
      setError(error?.response?.data?.msg || 'Error requesting withdrawal');
      setIsClaiming(false);
    },
  });

  const handleClaimRewards = useCallback(async () => {
    if (!id || balance < 5) return;

    try {
      setIsClaiming(true);
      setError(null);

      await requestWithdrawal.mutateAsync({
        userId: id,
        amount: String(ethers.parseEther(String(balance))),
      });
    } catch (err) {
      console.error('Error claiming rewards:', err);
      setError('Failed to claim rewards. Please try again.');
      setIsClaiming(false);
    }
  }, [id, balance, requestWithdrawal]);

  if (isLoading) {
    return (
      <Card title='Claim Your Rewards' className='m-0 h-full'>
        <div className='flex items-center justify-center py-12'>
          <Spinner />
        </div>
      </Card>
    );
  }

  return (
    <Card title='Claim Your Rewards' className='m-0 h-full flex flex-col'>
      <div className='p-5 flex-1 flex flex-col justify-center'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6 items-center'>
          {/* Balance Display */}
          <div className='flex items-center gap-4'>
            <div className='flex items-center justify-center w-16 h-16 rounded-xl bg-green-500/10'>
              <Icon className='w-8 h-8 fill-green-500' name='wallet' />
            </div>
            <div>
              <div className='text-caption text-t-tertiary mb-1'>
                Available Balance
              </div>
              <div className='text-h4 font-semibold text-t-primary'>
                <CurrencyFormat value={balance} />
              </div>
              {balance < 5 && (
                <div className='text-caption text-t-tertiary mt-1'>
                  Minimum withdrawal: $5.00
                </div>
              )}
            </div>
          </div>

          {/* Claim Button */}
          <div className='flex flex-col gap-3'>
            <Button
              onClick={handleClaimRewards}
              disabled={balance < 5 || isClaiming || success}
              className='w-full'
            >
              {isClaiming ? (
                <>
                  <Spinner className='w-4 h-4 mr-2' />
                  Processing...
                </>
              ) : success ? (
                <>
                  <Icon className='w-4 h-4 fill-white mr-2' name='check' />
                  Claimed Successfully!
                </>
              ) : (
                <>
                  <Icon className='w-4 h-4 fill-white mr-2' name='wallet' />
                  Claim Rewards
                </>
              )}
            </Button>

            {error && (
              <div className='flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20'>
                <Icon
                  className='w-4 h-4 fill-red-500 shrink-0 mt-0.5'
                  name='close'
                />
                <div className='text-caption text-red-500'>{error}</div>
              </div>
            )}

            {success && (
              <div className='flex items-start gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/20'>
                <Icon
                  className='w-4 h-4 fill-green-500 shrink-0 mt-0.5'
                  name='check'
                />
                <div className='text-caption text-green-500'>
                  Withdrawal request submitted successfully!
                </div>
              </div>
            )}

            {balance < 5 && (
              <div className='flex items-start gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20'>
                <Icon
                  className='w-4 h-4 fill-yellow-500 shrink-0 mt-0.5'
                  name='info'
                />
                <div className='text-caption text-yellow-600'>
                  You need at least $5.00 in available balance to claim rewards.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default ClaimRewards;
